import React from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import CassetteLoader from './CassetteLoader';
import {
  filterFilesForCategory,
  getUploadAcceptConfig,
  resolveUploadFileCategory,
  sanitizeFilename,
  type UploadFileCategory,
} from '../utils/security';
import { classNames } from '../utils/classNames';

function isMobile() {
  if (typeof navigator === 'undefined') return false;
  return /Mobi|Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent);
}

interface FileUploadProps {
  label: string;
  fileCategory?: UploadFileCategory;
  /** @deprecated use fileCategory */
  accept?: string;
  onFile: (files: FileList | File[]) => void;
  multiple?: boolean;
  uploadProgress?: number;
  uploading?: boolean;
  onRemove?: (index: number) => void;
  showPreview?: boolean;
  disabled?: boolean;
  className?: string;
  fill?: boolean;
  'data-testid'?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  label,
  fileCategory,
  accept,
  onFile,
  multiple,
  uploadProgress,
  uploading,
  onRemove,
  showPreview = true,
  disabled = false,
  className,
  fill = false,
  'data-testid': dataTestId,
}) => {
  const mobile = isMobile();
  const { t } = useTranslation();
  const [selectedFiles, setSelectedFiles] = React.useState<File[]>([]);
  const resolvedCategory = resolveUploadFileCategory(fileCategory, accept);
  const acceptConfig = getUploadAcceptConfig(fileCategory, accept);
  const acceptHint = acceptConfig.hint;

  const processFiles = React.useCallback(
    (files: File[]) => {
      files.forEach((file) => {
        const sanitizedName = sanitizeFilename(file.name);
        if (sanitizedName !== file.name) {
          console.warn(`Filename sanitized: ${file.name} -> ${sanitizedName}`);
        }
      });

      const { validFiles, errors } = filterFilesForCategory(files, resolvedCategory);

      if (errors.length > 0) {
        const errorMessage =
          errors.length === 1
            ? errors[0]
            : `${errors.length} files have errors. ${errors.slice(0, 3).join('; ')}${errors.length > 3 ? '...' : ''}`;
        toast.error(errorMessage);
      }

      if (validFiles.length > 0) {
        setSelectedFiles(validFiles);
        onFile(validFiles);
        if (validFiles.length < files.length) {
          toast.success(`${validFiles.length} valid file(s) selected`);
        }
      } else if (files.length > 0) {
        toast.error(`No valid files selected. ${acceptHint}.`);
      }
    },
    [resolvedCategory, onFile, acceptHint],
  );

  const onDrop = React.useCallback(
    (acceptedFiles: File[]) => {
      processFiles(acceptedFiles);
    },
    [processFiles],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptConfig.dropzone,
    multiple,
    noDrag: mobile,
    disabled,
  });

  const handleRemove = (index: number) => {
    const newFiles = [...selectedFiles];
    newFiles.splice(index, 1);
    setSelectedFiles(newFiles);
    onFile(newFiles);
    if (onRemove) onRemove(index);
  };

  React.useEffect(() => {
    setSelectedFiles([]);
  }, [label]);

  const actionLabel = mobile
    ? t('fileUpload.tapToUpload', 'Tap to upload')
    : t('fileUpload.selectFile');
  const hintLabel = mobile
    ? acceptHint
    : isDragActive
      ? t('fileUpload.dropHere') || 'Drop files here'
      : acceptHint;

  return (
    <div className={classNames('w-full', className)}>
      <div
        {...getRootProps()}
        className={classNames(
          'border-2 border-dashed rounded-xl text-center transition-all duration-200 flex flex-col items-center justify-center gap-2',
          fill ? 'w-full h-full min-h-[9rem]' : mobile ? 'p-6 min-h-[9rem]' : 'p-4 min-h-[7rem]',
          disabled
            ? 'opacity-50 cursor-not-allowed border-[var(--color-border)] bg-[var(--color-hover)]'
            : isDragActive
              ? 'border-[var(--brand-from)] bg-[var(--brand-from)]/10 scale-[1.02] shadow-lg cursor-pointer'
              : 'border-[var(--color-border)] bg-[var(--color-hover)] hover:border-[var(--brand-from)]/50 active:scale-[0.99] cursor-pointer',
        )}
      >
        <input {...getInputProps()} data-testid={dataTestId} />
        <ArrowUpTrayIcon
          className={`${mobile ? 'w-8 h-8' : 'w-6 h-6 mx-auto'} transition-colors ${
            isDragActive ? 'text-[var(--brand-from)]' : 'text-[var(--color-muted)]'
          }`}
          aria-hidden
        />
        <p
          className={`text-sm font-medium transition-colors ${
            isDragActive ? 'text-[var(--brand-from)]' : 'text-[var(--color-text)]'
          }`}
        >
          {isDragActive ? t('fileUpload.dropHere') || 'Drop files here' : label}
        </p>
        <div
          className={`text-xs transition-colors ${
            isDragActive ? 'text-[var(--brand-from)] font-semibold' : 'text-[var(--color-muted)]'
          }`}
        >
          {isDragActive && !mobile ? 'Release to upload' : hintLabel}
        </div>
        {!isDragActive && (
          <p className="text-xs font-semibold text-[var(--brand-from)] pointer-events-none">
            {actionLabel}
          </p>
        )}
      </div>
      {showPreview && selectedFiles.length > 0 && (
        <ul className="mt-2 text-xs text-[var(--color-muted)]">
          {selectedFiles.map((file, idx) => (
            <li key={file.name + idx} className="flex items-center justify-between">
              <span className="truncate">{file.name}</span>
              {uploading && idx === 0 && (
                <span className="ml-2 text-primary-500 flex items-center">
                  <CassetteLoader size={48} variant="inline" />
                  {t('fileUpload.uploading')}
                  {typeof uploadProgress === 'number' && <span className="ml-1">{uploadProgress}%</span>}
                </span>
              )}
              <button
                type="button"
                className="ml-2 text-red-500 hover:text-red-700 text-xs font-medium"
                onClick={() => handleRemove(idx)}
              >
                {t('buttons.remove')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FileUpload;
